/**
 * Per-chart layout context: the chart's OWN width (a chart in a 4-up
 * column is far narrower than the embed), print mode, and whether the
 * pointer is coarse. Feed `ctx` into the option builders in
 * `chart-layout.ts` / `echarts-base.ts`; bind `chartRef` to `<v-chart>`.
 *
 * Width is quantised to 16px so the option only rebuilds when a layout
 * value could actually change — vue-echarts replaces the whole option
 * on every rebuild, which resets legend toggles and zoom.
 */

import { computed, ref, type ComputedRef, type Ref } from "vue";

import { useElementWidth, useEmbedWidth, useMediaQuery } from "../sbi/embed-width.ts";
import { usePrintMode } from "../sbi/print-mode.ts";
import { WIDTH_STEP, type LayoutCtx } from "./chart-layout";

export function useChartLayout(): {
  chartRef: Ref<{ $el?: unknown } | null>;
  ctx: ComputedRef<LayoutCtx>;
} {
  const chartRef = ref<{ $el?: unknown } | null>(null);
  const embed = useEmbedWidth();
  // Until the chart mounts, the embed width is the best guess — right
  // for single-column layouts, so the first option already fits.
  const width = useElementWidth(chartRef, {
    step: WIDTH_STEP,
    initial: () => embed.width.value,
  });
  const coarse = useMediaQuery("(pointer: coarse)");
  const isPrint = usePrintMode();
  const ctx = computed<LayoutCtx>(() => ({
    width: width.value,
    print: isPrint(),
    coarse: !isPrint() && coarse.value,
  }));
  return { chartRef, ctx };
}
